import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { uuid } from '../types/types.ts';
import { createUuid } from '../utilities';
import { devtools } from 'zustand/middleware';
import type {} from '@redux-devtools/extension';
import type { KV } from '../types/types';
import { indexedDbStorage } from '../utilities/indexedDbStorage';
import { useTaskListStore, TRASH_LIST_ID } from './useTaskListStore';

type ItemState = {
  id: uuid;
  title: string; // use the first line (and limit to 40 characters) of the content as the title
  content: string; // markdown string
  listId: uuid; // each item must belong to a list
  keys?: KV; // an maps of strings, internal usage
  completed?: boolean;
  justCompleted?: boolean;
  failed?: boolean;
  justFailed?: boolean;
  justUpdated?: boolean;

  createdDate: Date;
  updatedDate?: Date;

  deleted?: boolean;
  deletedAt?: string;
  originalListId?: uuid;
};

type ItemStore = {
  items: Record<uuid, ItemState>;
  // only active item is rendered using the Editor and all other items are rendered using the Viewer
  activeItemId: uuid | null;
  // nextActiveItemId is used to record the next active item id, often the item is not yet created
  // this variable should be null most of the time.
  nextActiveItemId: uuid | null;

  // Each item has its own editing state, which is independent from others
  // isGlobalReadOnly is true when the global read only mode is on: e.g. when dragging starts, we set all items to read only
  // when dragging ends, we set it to false. Dndkit would easily crash when editable content is present.
  isGlobalReadOnly: boolean;
};

type ItemStoreAction = {
  updateContent: (
    id: uuid,
    content?: string,
    title?: string,
    listId?: uuid,
    keys?: KV
  ) => void;

  setActiveItemId: (id: uuid | null) => void;
  setNextActiveItemId: (id: uuid) => void;

  enableGlobalReadOnly: () => void;
  disableGlobalReadOnly: () => void;

  toggleComplete: (id: uuid) => void;
  clearJustCompleted: (id: uuid) => void;

  toggleFail: (id: uuid) => void;
  clearJustFailed: (id: uuid) => void;
  clearJustUpdated: (id: uuid) => void;

  deleteItem: (id: uuid) => void;
  restoreItem: (id: uuid) => void;
  reset: () => void;
  emptyTrash: () => void;
};

export const extractTitleFromMarkdown = (markdown: string): string => {
  const trimmed = markdown.trim();
  if (!trimmed) {
    return '';
  }

  const lines = trimmed.split('\n');
  let firstLine = '';
  for (const line of lines) {
    const cleanedLine = line.trim();
    if (cleanedLine !== '') {
      firstLine = cleanedLine;
      break;
    }
  }

  if (!firstLine) {
    return '';
  }

  // Strip markdown heading markers (e.g. #, ##, etc.)
  let title = firstLine.replace(/^#+\s+/, '');
  // Strip checklist brackets (e.g. - [ ], - [x], * [ ], etc.)
  title = title.replace(/^[-*+]\s+\[[ xX]\]\s+/, '');
  // Strip bullet/ordered list prefixes (e.g. -, *, +, 1., 1))
  title = title.replace(/^([-*+]|\d+[.)])\s+/, '');
  // Strip bold/italic/strikethrough styles (e.g. **, *, __, _, ~~)
  title = title.replace(/[*_~`]/g, '');
  // Strip links (e.g. [text](url) -> text)
  title = title.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  title = title.trim();

  // Limit length to a reasonable amount, e.g. 50 characters, and add ellipsis if truncated
  if (title.length > 50) {
    return title.slice(0, 50) + '...';
  }

  return title;
};

export const syncTitleToContent = (
  content: string,
  newTitle: string
): string => {
  const trimmed = content.trim();
  if (!trimmed) {
    return `# ${newTitle}`;
  }
  const lines = content.split('\n');

  // Find the first non-empty line
  let firstLineIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() !== '') {
      firstLineIndex = i;
      break;
    }
  }

  if (firstLineIndex === -1) {
    return `# ${newTitle}`;
  }

  const firstLine = lines[firstLineIndex];

  // Identify prefix (heading markers, list markers)
  const prefixMatch = firstLine.match(
    /^(\s*(?:#+\s+|[-*+]\s+\[[ xX]\]\s+|[-*+]\s+|\d+[.)]\s+)*)/
  );
  const prefix = prefixMatch ? prefixMatch[1] : '';

  // Construct the new first line
  lines[firstLineIndex] = `${prefix}${newTitle}`;

  return lines.join('\n');
};

// Create a store of the type State & Actions
export const useTaskItemStore = create<ItemStore & ItemStoreAction>()(
  devtools(
    persist(
      (set, get) => ({
        items: {},
        activeItemId: null,
        nextActiveItemId: null,
        isGlobalReadOnly: false,

        updateContent: (id, content, title, listId, keys) => {
          set((state) => {
            const item = state.items[id];

            const currentContent = item?.content ?? '';
            const currentTitle = item?.title ?? '';

            let nextContent = content !== undefined ? content : currentContent;
            let nextTitle = title !== undefined ? title : currentTitle;

            // Case 1: Content is updated (from Tiptap). Extract title.
            if (content !== undefined && title === undefined) {
              nextTitle = extractTitleFromMarkdown(content);
            }

            // Case 2: Title is updated (from list double-click). Sync back to content.
            if (
              title !== undefined &&
              title !== item?.title &&
              content === undefined
            ) {
              nextContent = syncTitleToContent(currentContent, title);
            }

            const isContentUpdated = content !== undefined && content !== currentContent;
            const isTitleUpdated = title !== undefined && title !== currentTitle;
            const hasChanged = isContentUpdated || isTitleUpdated;

            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  id,
                  content: nextContent,
                  title: nextTitle,
                  listId: listId || item?.listId,
                  keys: { ...(item?.keys || {}), ...keys },
                  updatedDate: new Date(),
                  createdDate: item?.createdDate || new Date(),
                  justUpdated: hasChanged ? true : (item?.justUpdated ?? false),
                },
              },
            };
          });
        },
        // setActive: (id: uuid, isActive: boolean) => {
        //   set((state) => {
        //     const item = state.items[id];
        //     return {
        //       items: { ...state.items, [id]: { ...item, isActive } },
        //     };
        //   });
        // }
        /// This will set the active item to the given id, and set all other items to inactive
        /// and clear the nextActiveItemId since the activation just completes
        setActiveItemId: (id) => {
          set((state) => {
            if (!id) return { activeItemId: null, nextActiveItemId: null };
            const item = state.items[id];
            if (item && item.justUpdated) {
              return {
                activeItemId: id,
                nextActiveItemId: null,
                items: {
                  ...state.items,
                  [id]: {
                    ...item,
                    justUpdated: false,
                  },
                },
              };
            }
            return { activeItemId: id, nextActiveItemId: null };
          });
        },

        setNextActiveItemId: (id) => {
          set({ nextActiveItemId: id });
        },

        enableGlobalReadOnly: () => {
          set({ isGlobalReadOnly: true });
        },
        disableGlobalReadOnly: () => {
          set({ isGlobalReadOnly: false });
        },

        toggleComplete: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item) return state;
            const nextCompleted = !item.completed;
            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  completed: nextCompleted,
                  justCompleted: nextCompleted,
                  failed: false,
                  justFailed: false,
                  updatedDate: new Date(),
                },
              },
            };
          });
        },

        clearJustCompleted: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item || !item.justCompleted) return {};
            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  justCompleted: false,
                },
              },
            };
          });
        },

        toggleFail: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item) return state;
            const nextFailed = !item.failed;
            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  failed: nextFailed,
                  justFailed: nextFailed,
                  completed: false,
                  justCompleted: false,
                  updatedDate: new Date(),
                },
              },
            };
          });
        },

        clearJustFailed: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item || !item.justFailed) return {};
            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  justFailed: false,
                },
              },
            };
          });
        },

        clearJustUpdated: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item || !item.justUpdated) return {};
            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  justUpdated: false,
                },
              },
            };
          });
        },

        deleteItem: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item) return {};
            const originalListId = item.listId;

            // 1. Remove from its current list in useTaskListStore
            const taskListStore = useTaskListStore.getState();
            if (originalListId) {
              taskListStore.removeItemFromItemsMap(originalListId, id);
            }

            const isEmptyNewTask =
              (!item.content || !item.content.trim()) &&
              (!item.title ||
                item.title.trim() === '' ||
                /^new task\s*(\d*)$/i.test(item.title.trim()));

            if (isEmptyNewTask) {
              // Clear active selection if the deleted empty task is the currently active one
              const { [id]: _, ...remainingItems } = state.items;
              const activeItemId = state.activeItemId;
              return {
                activeItemId: activeItemId === id ? null : activeItemId,
                items: remainingItems,
              };
            }

            // 2. Add to the Trash list in useTaskListStore
            taskListStore.updateItemsMap((currentMap) => {
              const trashItems = currentMap[TRASH_LIST_ID] || [];
              if (!trashItems.includes(id)) {
                return {
                  ...currentMap,
                  [TRASH_LIST_ID]: [...trashItems, id],
                };
              }
              return currentMap;
            });

            // 3. Mark the item as deleted in item store, and clear active selection if it matches the deleted task
            const activeItemId = state.activeItemId;
            return {
              activeItemId: activeItemId === id ? null : activeItemId,
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  deleted: true,
                  deletedAt: new Date().toISOString(),
                  originalListId: originalListId,
                  listId: TRASH_LIST_ID,
                },
              },
            };
          });
        },

        restoreItem: (id) => {
          set((state) => {
            const item = state.items[id];
            if (!item) return {};
            const targetListId =
              item.originalListId || 'c3565f9e-50e4-4a96-966b-ba95ea2c3292'; // fallback to Inbox list id
            const taskListStore = useTaskListStore.getState();
            // 1. Remove from the Trash list
            taskListStore.removeItemFromItemsMap(TRASH_LIST_ID, id);
            // 2. Add back to the target list
            taskListStore.updateItemsMap((currentMap) => {
              const listItems = currentMap[targetListId] || [];
              if (!listItems.includes(id)) {
                return {
                  ...currentMap,
                  [targetListId]: [...listItems, id],
                };
              }
              return currentMap;
            });
            // 3. Update the item state in item store
            return {
              items: {
                ...state.items,
                [id]: {
                  ...item,
                  deleted: false,
                  deletedAt: undefined,
                  listId: targetListId,
                },
              },
            };
          });
        },

        reset: () => {
          set({
            items: {},
            activeItemId: null,
            nextActiveItemId: null,
            isGlobalReadOnly: false,
          });
        },

        emptyTrash: () => {
          set((state) => {
            const remainingItems = { ...state.items };
            Object.keys(remainingItems).forEach((id) => {
              if (remainingItems[id]?.deleted) {
                delete remainingItems[id];
              }
            });
            const taskListStore = useTaskListStore.getState();
            taskListStore.updateItemsMap((currentMap) => {
              return {
                ...currentMap,
                [TRASH_LIST_ID]: [],
              };
            });
            const activeItemId = state.activeItemId;
            const shouldClearActive = activeItemId
              ? !!state.items[activeItemId]?.deleted
              : false;
            return {
              items: remainingItems,
              activeItemId: shouldClearActive ? null : activeItemId,
            };
          });
        },
      }),
      {
        name: 'task-item-storage',
        storage: createJSONStorage(() => indexedDbStorage),
      }
    )
  )
);
